/**
 * Shared utility for projecting geographic coordinates (lat/lng) onto an SVG
 * icon canvas while preserving the real-world aspect ratio.
 *
 * The previous per-file implementations scaled X and Y independently to fill
 * the full icon area, which distorted shapes (e.g. a long narrow path would
 * render as a square). This utility derives a single uniform scale factor from
 * the larger of the two real-world extents (with cos(lat) correction for
 * longitude) and centers the result within the icon container.
 */

/**
 * Projects an array of { location: { latitude, longitude } } point objects
 * onto an SVG coordinate string (e.g. "x1,y1 x2,y2 ...") that preserves the
 * geographic aspect ratio.
 *
 * @param points   Array of objects with `.location.latitude` / `.location.longitude`
 * @param viewBox  Size of the square SVG viewBox (default 200)
 * @param padding  Padding inside the viewBox on each side (default 10, giving a usable 10–190 range)
 * @returns        SVG-compatible points string, or '' if input is empty/null
 */
export function scaleGeoPointsToSvg(
  points: any[] | null | undefined,
  viewBox: number = 200,
  padding: number = 10,
): string {
  if (!points || points.length === 0) return "";

  // Filter out any entries with missing location data
  const valid = points.filter(
    (p: any) => p?.location?.latitude != null && p?.location?.longitude != null,
  );
  if (valid.length === 0) return "";

  const lats: number[] = valid.map((p: any) => p.location.latitude);
  const lngs: number[] = valid.map((p: any) => p.location.longitude);

  return projectCoordsToSvg(lats, lngs, viewBox, padding);
}

/**
 * Same as `scaleGeoPointsToSvg` but accepts two parallel arrays of raw
 * latitude and longitude values instead of point objects.  Used by callers
 * that have already extracted the coordinate arrays (e.g. MissionsSubpanel
 * `getMissionItem2`).
 */
export function scaleRawCoordsToSvg(
  latValues: number[],
  lngValues: number[],
  viewBox: number = 200,
  padding: number = 10,
): string {
  if (
    !latValues ||
    !lngValues ||
    latValues.length === 0 ||
    lngValues.length === 0 ||
    latValues.length !== lngValues.length
  ) {
    return "";
  }
  return projectCoordsToSvg(latValues, lngValues, viewBox, padding);
}

// ---------------------------------------------------------------------------
// Internal helper
// ---------------------------------------------------------------------------

function projectCoordsToSvg(
  lats: number[],
  lngs: number[],
  viewBox: number,
  padding: number,
): string {
  const latMin = Math.min(...lats);
  const latMax = Math.max(...lats);
  const lngMin = Math.min(...lngs);
  const lngMax = Math.max(...lngs);

  const latRange = latMax - latMin;
  const lngRange = lngMax - lngMin;

  // cos(latitude) correction so that one degree of longitude is comparable
  // in real-world distance to one degree of latitude.
  const avgLat = (latMin + latMax) / 2;
  const cosLat = Math.cos((avgLat * Math.PI) / 180);

  // Real-world extents in "latitude-degree" units
  const xExtent = lngRange * cosLat;
  const yExtent = latRange;

  const canvasSize = viewBox - 2 * padding;

  // Degenerate case: all points coincide → place a dot in the center
  if (xExtent === 0 && yExtent === 0) {
    const center = viewBox / 2;
    return lats.map(() => `${center},${center}`).join(" ");
  }

  // Uniform scale: fit the larger extent into canvasSize
  const scale = canvasSize / Math.max(xExtent, yExtent);

  // Offsets to center the smaller dimension within the canvas
  const xOffset = padding + (canvasSize - xExtent * scale) / 2;
  const yOffset = padding + (canvasSize - yExtent * scale) / 2;

  return lats
    .map((lat, i) => {
      const lng = lngs[i];
      const x = (lng - lngMin) * cosLat * scale + xOffset;
      // Invert latitude so that north (higher lat) is at the top of the icon
      const y = (latMax - lat) * scale + yOffset;
      return `${x},${y}`;
    })
    .join(" ");
}
